import { valueIsDefined } from '@netpurpose/utils'
import { pickBy } from 'lodash'
import type {
  PortfolioSnapshot as PortfolioSnapshotBE,
  PortfolioWithCoverage,
  PortfolioWithEntityDetail,
} from '../../generated/facts'
import type { ReverseFieldMap } from '../../queryBuilder'
import type { Camelize } from '../../utils'
import { formatDateForBackend, parseDateFromBackend } from '../datetime'

export type PortfolioType = PortfolioWithCoverage['type']

// The backend sends `lcu` and `lcu_total_value` on portfolio responses, but the OpenAPI schema
// for PortfolioWithCoverage does not yet expose them. Augment the camelized type here until the
// schema is updated upstream.
type PortfolioWithCoverageCamelized = Camelize<PortfolioWithCoverage> & {
  lcu?: string | null
  lcuTotalValue?: number | null
}

export type Portfolio = Omit<
  PortfolioWithCoverageCamelized,
  | 'portfolioId'
  | 'valuationDate'
  | 'totalValue'
  | 'lcuTotalValue'
  | 'valPctCovered'
  | 'valPctResearched'
  | 'funds'
  | 'lcu'
> & {
  id: number
  valuationDate: Date
  value?: number
  lcu?: string | null
  lcuValue?: number
  valueCovered?: number
  valueResearched?: number
  funds?: Portfolio[]
}

export const transformPortfolio = <PortfolioProps extends PortfolioWithCoverageCamelized>({
  portfolioId,
  snapshotId,
  totalValue,
  valPctCovered,
  valPctResearched,
  valuationDate,
  funds,
  lcu,
  lcuTotalValue,
  ...rest
}: PortfolioProps): Portfolio => ({
  ...pickBy(
    {
      value: totalValue,
      lcuValue: lcuTotalValue,
      snapshotId,
      funds: funds?.map(transformPortfolio),
      lcu,
    },
    valueIsDefined,
  ),
  id: portfolioId,
  valueCovered: valPctCovered,
  valueResearched: valPctResearched,
  valuationDate: parseDateFromBackend(valuationDate),
  ...rest,
})

export const reversePortfolioFieldMap: ReverseFieldMap<keyof Portfolio> = {
  id: 'portfolio_id',
  snapshotId: 'snapshot_id',
  name: 'name',
  type: 'type',
  valuationDate: {
    key: 'valuation_date',
    transform: (date) => (date ? formatDateForBackend(date) : undefined),
  },
  value: 'total_value',
  lcu: 'lcu',
  lcuValue: 'lcu_total_value',
  valueCovered: 'val_pct_covered',
  valueResearched: 'val_pct_researched',
  numHoldings: 'num_holdings',
  numHoldingsCovered: 'num_holdings_covered',
  numHoldingsResearched: 'num_holdings_researched',
  funds: 'funds',
}

export type EntityPortfolio = Omit<
  Camelize<PortfolioWithEntityDetail>,
  'portfolioId' | 'valuationDate' | 'totalValue'
> & {
  id: number
  valuationDate: Date
  portfolioValue?: number
}

export const transformEntityPortfolio = <
  EntityPortfolioProps extends Camelize<PortfolioWithEntityDetail>,
>({
  portfolioId,
  snapshotId,
  totalValue,
  entityValue,
  valuationDate,
  ...rest
}: EntityPortfolioProps): EntityPortfolio => ({
  ...pickBy({ portfolioValue: totalValue, entityValue, snapshotId }, valueIsDefined),
  id: portfolioId,
  valuationDate: parseDateFromBackend(valuationDate),
  ...rest,
})

export const reverseEntityPortfolioFieldMap: ReverseFieldMap<keyof EntityPortfolio | 'searchTerm'> =
  {
    id: 'portfolio_id',
    snapshotId: 'snapshot_id',
    name: 'name',
    type: 'type',
    valuationDate: {
      key: 'valuation_date',
      transform: (date) => (date ? formatDateForBackend(date) : undefined),
    },
    portfolioValue: 'total_value',
    entityValue: 'entity_value',
    entityWeight: 'entity_weight',
    searchTerm: 'name',
  }

export type PortfolioSnapshot = Camelize<PortfolioSnapshotBE>

export const reversePortfolioSnapshotFieldMap: ReverseFieldMap<keyof PortfolioSnapshot> = {
  snapshotId: 'snapshot_id',
  valuationDate: 'valuation_date',
  totalValue: 'total_value',
  lcu: 'lcu',
  lcuTotalValue: 'lcu_total_value',
  createdAt: 'created_at',
  createdBy: 'created_by',
  isLatest: 'is_latest',
  numHoldings: 'num_holdings',
  totalWeight: 'total_weight',
}
